/**
 * «Кейсы» — Spec 025, D7: our solution examples, every one a «Пример решения»
 * (R-08). The chips answer where a case stands; «не использован на площадке…»
 * answers what may still go out there — a case goes to a place once. A row
 * says its number and title, where it stands in words and how often it went
 * out; on a computer it opens on the right.
 */

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Plus } from "lucide-react";

import { Cases } from "@/api/resources";
import type { CaseOut } from "@/api/types";
import { CaseState, EXAMPLE_LABEL, NewCaseSheet, PLACE_KIND } from "@/components/CaseForm";
import { Button } from "@/components/ui/Button";
import { SelectField } from "@/components/ui/Field";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Mark } from "@/components/ui/Status";
import { AppBar, ago, Chip, EmptyNote, ListPane, ScreenHeader, useIsDesktop, whoDid } from "@/kit";
import { cn } from "@/lib/cn";
import { pluralRu } from "@/lib/format";
import { nameOf, usePeople } from "@/lib/people";
import { useBackButton } from "@/lib/telegram-ui";
import { CaseScreen } from "@/screens/CaseScreen";

export const CASE_FILTERS = ["all", "draft", "review", "accepted", "archived"] as const;
export type CaseFilter = (typeof CASE_FILTERS)[number];

const FILTER_LABEL: Record<CaseFilter, string> = {
  all: "Все",
  draft: "Черновики",
  review: "На проверке",
  accepted: "Приняты",
  archived: "Архив",
};

const EMPTY: Record<CaseFilter, string> = {
  all: "Кейсов пока нет. Создай первый — или попроси агента: он напишет черновик и пришлёт на проверку.",
  draft: "Черновиков нет.",
  review: "На проверке ничего нет — всё решено.",
  accepted: "Принятых кейсов пока нет.",
  archived: "В архиве пусто.",
};

/** `?f=` of the address → the chip; anything else is «Все» (the link from «Решить» is `?f=review`). */
export function caseFilterOf(value: string | null): CaseFilter {
  return CASE_FILTERS.find((f) => f === value) ?? "all";
}

/** What a chip shows: «Все» is everything but the archive, as the server lists it. */
export function casesFor(filter: CaseFilter, cases: CaseOut[]): CaseOut[] {
  return filter === "all" ? cases.filter((c) => c.status !== "archived") : cases.filter((c) => c.status === filter);
}

/** «использован 2 раза»; an accepted case that never went out says so. */
export function usesLine(c: Pick<CaseOut, "uses_count" | "status">): string | null {
  if (c.uses_count > 0) return `использован ${c.uses_count} ${pluralRu(c.uses_count, "раз", "раза", "раз")}`;
  return c.status === "accepted" ? "ещё не использован" : null;
}

export function CasesScreen() {
  useBackButton("/knowledge");
  const desktop = useIsDesktop();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const people = usePeople();

  const filter = caseFilterOf(params.get("f"));
  const placeId = params.get("place") ?? "";
  const places = useQuery({ queryKey: ["cases", "places"], queryFn: () => Cases.places.list() });
  // One request serves the four chips of live cases; the archive loads when asked for.
  const live = useQuery({
    queryKey: ["cases", "list", "live", placeId],
    queryFn: () => Cases.list({ unused_at: placeId || undefined }),
  });
  const archive = useQuery({
    queryKey: ["cases", "list", "archived", placeId],
    queryFn: () => Cases.list({ status: "archived", unused_at: placeId || undefined }),
    enabled: filter === "archived",
  });
  const source = filter === "archived" ? archive : live;
  const shown = casesFor(filter, source.data ?? []);
  const inReview = casesFor("review", live.data ?? []).length;

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const open = params.get("open");
  const openCase = (id: string) => (desktop ? setParam("open", id) : navigate(`/knowledge/cases/${id}`));
  const countOf = (f: CaseFilter) => (f === "archived" ? archive.data?.length : live.data && casesFor(f, live.data).length);

  const list = (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/knowledge" context="Знания" />
      <ScreenHeader
        title="Кейсы"
        sub={inReview ? `${inReview} на проверке — прими или верни` : "Примеры решений: так мы решаем задачу"}
        trailing={
          <Button size="compact" variant="secondary" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setCreating(true)}>
            Кейс
          </Button>
        }
      />
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] lg:mx-0 lg:flex-wrap lg:px-0 [&::-webkit-scrollbar]:hidden">
        {CASE_FILTERS.map((f) => {
          const n = countOf(f);
          return (
            <Chip key={f} on={filter === f} onClick={() => setParam("f", f === "all" ? null : f)}>
              {FILTER_LABEL[f]} {n != null && <span className="font-medium">{n}</span>}
            </Chip>
          );
        })}
      </div>
      {(places.data?.length ?? 0) > 0 && (
        <SelectField
          label="Не использован на площадке…"
          placeholder="не важно"
          value={placeId}
          onChange={(value) => setParam("place", value || null)}
          options={places.data!.map((p) => ({ value: p.id, label: `${p.name} · ${PLACE_KIND[p.kind]}` }))}
        />
      )}
      {source.isLoading && <SkeletonList count={4} />}
      {source.isError && (
        <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void source.refetch()}>Повторить</Button>}>
          Не удалось загрузить кейсы.
        </EmptyNote>
      )}
      {source.data && shown.length === 0 && (
        <EmptyNote>{placeId ? "Нет кейсов, которые ещё не выходили на этой площадке." : EMPTY[filter]}</EmptyNote>
      )}
      {shown.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {shown.map((c) => {
            const meta = [c.audience, whoDid(nameOf(people.data, c.author_id), c.via_agent), usesLine(c), ago(c.updated_at)];
            return (
              <li key={c.id} className="border-b border-p-border last:border-0">
                <button type="button" onClick={() => openCase(c.id)}
                  className={cn("flex w-full items-start gap-3 px-4 py-3 text-left press-surface", open === c.id && "bg-p-surface-2")}>
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <CaseState status={c.status} />
                      <Mark>{c.label || EXAMPLE_LABEL}</Mark>
                    </span>
                    <span className="text-[15px] font-semibold leading-5 text-p-text">
                      <span className="text-p-text-muted">Кейс №{c.number}.</span> {c.title}
                    </span>
                    <span className="line-clamp-2 text-meta text-p-text-muted">{meta.filter(Boolean).join(" · ")}</span>
                  </span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <NewCaseSheet
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(c) => {
          setCreating(false);
          openCase(c.id);
        }}
      />
    </div>
  );

  if (!desktop) return list;
  return (
    <ListPane
      list={list}
      listWidth={400}
      open={Boolean(open)}
      onClose={() => setParam("open", null)}
      pane={open ? <CaseScreen key={open} caseId={open} embedded /> : null}
      placeholder={shown.length ? <EmptyNote>Выбери кейс слева — он откроется здесь.</EmptyNote> : null}
    />
  );
}
